/** Colour helpers: the design tokens are stored as HSL channels (Tailwind style). */

export interface HslChannels {
  h: number
  s: number
  l: number
}

export function parseHexColor(value: string): { r: number; g: number; b: number } | undefined {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim())
  if (!match) return undefined
  let hex = match[1]
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("")
  }
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  }
}

export function hexToHslChannels(value: string): string | undefined {
  const rgb = parseHexColor(value)
  if (!rgb) return undefined
  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6
        break
      case g:
        h = (b - r) / delta + 2
        break
      default:
        h = (r - g) / delta + 4
    }
    h *= 60
    if (h < 0) h += 360
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** Lightens (positive amount) or darkens (negative) HSL channel values. */
export function adjustHslLightness(channels: string, amount: number): string {
  const [h, s, l] = channels.split(" ")
  const lightness = Math.min(100, Math.max(0, parseFloat(l) + amount))
  return `${h} ${s} ${Math.round(lightness)}%`
}
